import {Octicon, type QueryBuilderElement} from '@github-ui/query-builder-element'

import {CustomFilterProvider} from '../providers/custom-filter-provider'
import {UserFilterProvider} from '../providers/user-filter-provider'
import {
  type FilterType,
  type FilterValueType,
  NegativeFilters,
  NOT_SHOWN,
  type UserFilterValueType,
} from './../constants/search-filters'

/**
 * Parse the string query into a map of search tokens
 * Values that don't contain a colon are treated as a search term and stored with the `searchTerms` key
 *
 * @param query GitHub search query
 * @returns Map of search tokens + search terms
 * @example
 * const queryMap = parseQuery('repo:github/github')
 * {
 *  repo: ['github.com'],
 *  searchTerms: []
 * }
 *
 * const queryMap = parseQuery('repo:github/github repo:github/issues state:open label:bug fun issue')
 * {
 *  repo: ['github/github', 'github/issues'],
 *  is: ['open'],
 *  label: ['bug']
 *  searchTerms: ['fun', 'issue']
 * }
 */
export function parseQuery(query: string): Map<string, string[]> {
  const queryMap: Map<string, string[]> = new Map()
  queryMap.set('searchTerms', [])

  const matchBySpaceIgnoreQuotedExp = /((?:[^\s"]+|"[^"]*")+)/g
  const tokens = query
    .trim()
    .split(matchBySpaceIgnoreQuotedExp)
    .filter(item => item.trim() !== '') // Remove duds

  for (const token of tokens) {
    const [key, value] = token.split(':')

    // Contains ':' so it's a filter token
    if (key && value) {
      if (!queryMap.has(key)) {
        queryMap.set(key, [value])
        continue
      }

      // We can have more than one usage of the filter, so we append if the key exists
      queryMap.set(key, [...queryMap.get(key)!, value])
      continue
    }

    // Values without ':' are considered search terms
    queryMap.set('searchTerms', [...queryMap.get('searchTerms')!, token])
  }

  return queryMap
}

// These are helper functions to create negative filter providers.
export function createFilter<A extends EventTarget>(
  provider: new (q: QueryBuilderElement, f: FilterType, v?: FilterValueType[]) => A,
  filterValue: FilterType,
  queryBuilder: QueryBuilderElement,
  values?: FilterValueType[],
) {
  if (filterValue.value in NegativeFilters) {
    const value = filterValue.value as keyof typeof NegativeFilters
    new provider(
      queryBuilder,
      {
        ...filterValue,
        name: NegativeFilters[value],
        value: `-${filterValue.value}`,
        valuesKey: filterValue.value,
        priority: NOT_SHOWN,
      },
      values,
    )
  }

  new provider(queryBuilder, filterValue, values)
}

export function createCustomFilter(
  filterValue: FilterType,
  queryBuilder: QueryBuilderElement,
  values: FilterValueType[],
) {
  if (filterValue.value in NegativeFilters) {
    const value = filterValue.value as keyof typeof NegativeFilters
    new CustomFilterProvider(
      queryBuilder,
      {
        ...filterValue,
        name: NegativeFilters[value],
        icon: Octicon.Not,
        value: `-${filterValue.value}`,
        valuesKey: filterValue.value,
        priority: NOT_SHOWN,
      },
      values,
    )
  }

  new CustomFilterProvider(queryBuilder, filterValue, values)
}

export function createUserFilter(
  filterValue: UserFilterValueType,
  queryBuilder: QueryBuilderElement,
  login: string,
  avatarUrl: string,
  scopedRepository?: string | undefined,
) {
  if (filterValue.value in NegativeFilters) {
    const value = filterValue.value as keyof typeof NegativeFilters
    new UserFilterProvider(
      queryBuilder,
      {
        ...filterValue,
        name: NegativeFilters[value],
        value: `-${filterValue.value}`,
        priority: NOT_SHOWN,
      },
      login,
      avatarUrl,
      scopedRepository,
    )
  }

  new UserFilterProvider(queryBuilder, filterValue, login, avatarUrl, scopedRepository)
}
